<template>
  <div class="schedule-page">
    <!-- Page Header -->
    <div class="schedule-header">
      <div class="schedule-header__left">
        <div class="schedule-header__icon">
          <font-awesome-icon
            :icon="['fas', 'calendar-check']"
            class="schedule-header__icon-svg"
          />
        </div>
        <div>
          <h1 class="schedule-header__title">
            Schedule Management
          </h1>
          <p class="schedule-header__subtitle">
            <font-awesome-icon
              :icon="['fas', 'clipboard-list']"
              class="mr-50"
            />
            Track and manage employee vacations &amp; leave requests
          </p>
        </div>
      </div>
      <div class="schedule-header__right">
        <button
          type="button"
          class="schedule-header__filter-btn"
          @click="filtersOpen = !filtersOpen"
        >
          <font-awesome-icon
            :icon="filtersOpen ? ['fas', 'chevron-up'] : ['fas', 'sliders']"
            class="schedule-header__filter-btn-icon"
          />
          <span>{{ filtersOpen ? 'Hide' : 'Filters' }}</span>
          <span
            v-if="filtersOpen"
            class="schedule-header__filter-btn-dot"
          />
        </button>
        <div class="schedule-header__badge">
          <font-awesome-icon
            :icon="['fas', 'users']"
            class="mr-50"
          />
          <span>Team Overview</span>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="schedule-stats">
      <div class="schedule-stat-card schedule-stat-card--total">
        <div class="schedule-stat-card__icon">
          <font-awesome-icon :icon="['fas', 'folder-open']" />
        </div>
        <div class="schedule-stat-card__body">
          <span class="schedule-stat-card__value">{{ totalRequests }}</span>
          <span class="schedule-stat-card__label">Total</span>
        </div>
      </div>
      <div class="schedule-stat-card schedule-stat-card--approved">
        <div class="schedule-stat-card__icon">
          <font-awesome-icon :icon="['fas', 'circle-check']" />
        </div>
        <div class="schedule-stat-card__body">
          <span class="schedule-stat-card__value">{{ approvedCount }}</span>
          <span class="schedule-stat-card__label">Approved</span>
        </div>
      </div>
      <div class="schedule-stat-card schedule-stat-card--pending">
        <div class="schedule-stat-card__icon">
          <font-awesome-icon :icon="['fas', 'hourglass-half']" />
        </div>
        <div class="schedule-stat-card__body">
          <span class="schedule-stat-card__value">{{ pendingCount }}</span>
          <span class="schedule-stat-card__label">Pending</span>
        </div>
      </div>
      <div class="schedule-stat-card schedule-stat-card--rejected">
        <div class="schedule-stat-card__icon">
          <font-awesome-icon :icon="['fas', 'circle-xmark']" />
        </div>
        <div class="schedule-stat-card__body">
          <span class="schedule-stat-card__value">{{ rejectedCount }}</span>
          <span class="schedule-stat-card__label">Rejected</span>
        </div>
      </div>
    </div>

    <!-- Filters (collapsible) -->
    <b-collapse
      v-model="filtersOpen"
      class="schedule-filters-collapse"
    >
      <div class="schedule-filters">
        <div class="schedule-filters__inner">
          <!-- Row 1: Status, Office, User(s) -->
          <div class="schedule-filters__row">
            <div class="schedule-filter schedule-filter--status">
              <label class="schedule-filter__label">Status</label>
              <div class="schedule-filter__field">
                <span class="schedule-filter__icon">
                  <font-awesome-icon :icon="['fas', 'circle-check']" />
                </span>
                <b-form-select
                  v-model="filterStatus"
                  :options="statusOptions"
                  class="schedule-filter__input schedule-filter__select"
                  @change="applyFilters"
                />
              </div>
            </div>
            <div class="schedule-filter schedule-filter--office">
              <label class="schedule-filter__label">Office</label>
              <div class="schedule-filter__field">
                <span class="schedule-filter__icon">
                  <font-awesome-icon :icon="['fas', 'building']" />
                </span>
                <b-form-select
                  v-model="filterOfficeId"
                  :options="officeOptions"
                  value-field="value"
                  text-field="text"
                  class="schedule-filter__input schedule-filter__select"
                  @change="applyFilters"
                />
              </div>
            </div>
            <div class="schedule-filter schedule-filter--user">
              <label class="schedule-filter__label">User(s)</label>
              <div class="schedule-filter__field">
                <span class="schedule-filter__icon">
                  <font-awesome-icon :icon="['fas', 'users']" />
                </span>
                <v-select
                  v-model="filterUsers"
                  :options="userOptions"
                  :reduce="opt => opt.value"
                  label="label"
                  placeholder="Select user(s)"
                  class="schedule-filter__vselect"
                  multiple
                  @input="applyFilters"
                />
              </div>
            </div>
          </div>
          <!-- Row 2: From, To + Clear button -->
          <div class="schedule-filters__row schedule-filters__row--second">
            <div class="schedule-filter schedule-filter--date">
              <label class="schedule-filter__label">From date</label>
              <div class="schedule-filter__field">
                <span class="schedule-filter__icon">
                  <font-awesome-icon :icon="['fas', 'calendar-day']" />
                </span>
                <b-form-datepicker
                  v-model="filterFrom"
                  placeholder="From date"
                  class="schedule-filter__input"
                  menu-class="schedule-datepicker-dropdown"
                  today-button
                  reset-button
                  close-button
                  locale="en"
                  @input="applyFilters"
                />
              </div>
            </div>
            <div class="schedule-filter schedule-filter--date">
              <label class="schedule-filter__label">To date</label>
              <div class="schedule-filter__field">
                <span class="schedule-filter__icon">
                  <font-awesome-icon :icon="['fas', 'calendar-check']" />
                </span>
                <b-form-datepicker
                  v-model="filterTo"
                  placeholder="To date"
                  class="schedule-filter__input"
                  menu-class="schedule-datepicker-dropdown"
                  today-button
                  reset-button
                  close-button
                  locale="en"
                  @input="applyFilters"
                />
              </div>
            </div>
            <div class="schedule-filter__clear-wrap">
              <b-button
                variant="outline-secondary"
                size="sm"
                class="schedule-filter__clear"
                @click="clearFilters"
              >
                <font-awesome-icon
                  :icon="['fas', 'rotate-right']"
                  class="schedule-filter__clear-icon"
                />
                Clear filters
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </b-collapse>

    <!-- Table -->
    <div class="schedule-table-wrap">
      <general-table
        ref="generalTable"
        :api-url="apiUrl"
        :is-request-table="true"
        :normal-search="false"
        :view-search-input="false"
        :delete-content="true"
        :block-content="false"
        :edit-content="false"
        :view-content="true"
        :viw-component="'show-schedule'"
        :columns="columns"
        :type="type"
        :top-header="topHeader"
        @data-loaded="computeStats"
      >
        <!-- User Info Cell -->
        <template #cell(user_name)="data">
          <div
            class="schedule-user"
            @click="handleCurrentLeavesBalance(data.item.current_leaves_balance)"
          >
            <div class="schedule-user__avatar">
              <b-avatar
                size="42"
                variant="light-primary"
                :text="getInitials(data.item.user && data.item.user.name)"
              />
              <span class="schedule-user__status-dot" />
            </div>
            <div class="schedule-user__info">
              <span class="schedule-user__name">{{ data.item.user && data.item.user.name }}</span>
              <span class="schedule-user__email">
                <font-awesome-icon
                  :icon="['far', 'envelope']"
                  class="mr-25"
                />
                {{ data.item.user && data.item.user.email }}
              </span>
            </div>
          </div>
        </template>

        <!-- Leave Type Cell -->
        <template #cell(type)="data">
          <div class="schedule-type-badge">
            <font-awesome-icon
              :icon="getTypeIcon(data.item.type)"
              class="schedule-type-badge__icon"
            />
            <span v-if="data.item.type !== 'other'">{{ data.item.type }}</span>
            <span v-else>{{ data.item.other_type }}</span>
          </div>
        </template>

        <!-- Status Cell -->
        <template #cell(status)="data">
          <span
            class="schedule-status"
            :class="'schedule-status--' + (data.item.status || 'pending')"
          >
            <font-awesome-icon
              :icon="getStatusIcon(data.item.status)"
              class="mr-25"
            />
            <span class="text-capitalize">{{ data.item.status || 'pending' }}</span>
          </span>
        </template>

        <!-- Days Count Cell -->
        <template #cell(days_count)="data">
          <div class="schedule-days">
            <font-awesome-icon
              :icon="['far', 'calendar-days']"
              class="schedule-days__icon"
            />
            <span class="schedule-days__value">{{ data.item.days_count }}</span>
            <span class="schedule-days__label">{{ data.item.days_count === 1 ? 'day' : 'days' }}</span>
          </div>
        </template>

        <!-- Start Date Cell -->
        <template #cell(start_date)="data">
          <div class="schedule-date">
            <div class="schedule-date__icon-wrap schedule-date__icon-wrap--start">
              <font-awesome-icon :icon="['fas', 'arrow-right-to-bracket']" />
            </div>
            <span class="schedule-date__text">{{ handleFormData(data.item.start_date) }}</span>
          </div>
        </template>

        <!-- End Date Cell -->
        <template #cell(end_date)="data">
          <div class="schedule-date">
            <div class="schedule-date__icon-wrap schedule-date__icon-wrap--end">
              <font-awesome-icon :icon="['fas', 'arrow-right-from-bracket']" />
            </div>
            <span class="schedule-date__text">{{ handleFormData(data.item.end_date) }}</span>
          </div>
        </template>

        <!-- Start Date Cell -->
        <template #cell(start_time)="data">
          <div
            v-if="data.item.start_time"
            class="schedule-date"
          >
            <div class="schedule-date__icon-wrap schedule-date__icon-wrap--start">
              <font-awesome-icon :icon="['fas', 'arrow-right-to-bracket']" />
            </div>
            <span class="schedule-date__text">{{ data.item.start_time }}</span>
          </div>
        </template>

        <!-- End Date Cell -->
        <template #cell(end_time)="data">
          <div
            v-if="data.item.end_time"
            class="schedule-date"
          >
            <div class="schedule-date__icon-wrap schedule-date__icon-wrap--end">
              <font-awesome-icon :icon="['fas', 'arrow-right-from-bracket']" />
            </div>
            <span class="schedule-date__text">{{ data.item.end_time }}</span>
          </div>
        </template>

        <!-- Balance Cell -->
        <template #cell(current_leaves_balance)="data">
          <button
            class="schedule-balance-btn"
            @click="handleCurrentLeavesBalance(data.item.current_leaves_balance)"
          >
            <font-awesome-icon
              :icon="['fas', 'scale-balanced']"
              class="mr-50"
            />
            <span>View Balance</span>
          </button>
        </template>
      </general-table>
    </div>
  </div>
</template>

<script>
import VSelect from 'vue-select'
import GeneralTable from '@/components/general-table/GeneralTable.vue'
import openApproval from './control-schedule/openApproval'
import currentLeavesBalance from './control-schedule/CurrentLeavesBalance'
import formatDate from '@/global-compositions/formatDate'

export default {
  components: { GeneralTable, VSelect },
  data() {
    return {
      APIURL: 'vacation',
      viewContent: true,
      type: 'page',
      partners: [],
      filter: {},
      filterFrom: '',
      filterTo: '',
      filterStatus: '',
      filterOfficeId: null,
      filterUsers: [],
      types: '',
      loading: false,
      totalRequests: 0,
      approvedCount: 0,
      pendingCount: 0,
      rejectedCount: 0,
      filtersOpen: false,
      statusOptions: [
        { value: '', text: 'All statuses' },
        { value: 'approved', text: 'Approved' },
        { value: 'pending', text: 'Pending' },
        { value: 'rejected', text: 'Rejected' },
      ],
      columns: [
        { key: 'user_name', label: '' },
        { key: 'type', label: 'Type' },
        { key: 'status', label: 'Status' },
        { key: 'days_count', label: 'Days Count' },
        { key: 'start_date', label: 'Start Date' },
        { key: 'end_date', label: 'End Date' },
        { key: 'start_time', label: 'Start Time' },
        { key: 'end_time', label: 'End Time' },
        { key: 'current_leaves_balance', label: 'Balance' },
        { key: 'actions' },
      ],
      topHeader: [
        { lable: 'User Info', colspan: 1 },
        { lable: 'Schedule Info', colspan: 3 },
        { lable: 'Date', colspan: 4 },
        { lable: 'Actions', colspan: 2 },
      ],
    }
  },

  computed: {
    officeOptions() {
      const list = this.$store.getters['generalData/offices/offices'] || []
      return list.length ? list : [{ value: null, text: 'Select Office' }]
    },
    userOptions() {
      return this.$store.getters['generalData/assignList/assignList'] || []
    },
    // Same logic as FilterRequest: table uses buildParams from $route.query; apiUrl is base only
    apiUrl() {
      return 'vacation'
    },
  },

  watch: {
    '$route.query': {
      handler(query) {
        this.filterFrom = query.from || ''
        this.filterTo = query.to || ''
        this.filterStatus = query.status || ''
        this.filterOfficeId = query.office != null && query.office !== '' ? query.office : null
        const userIdParam = query.user_id
        if (!userIdParam) {
          this.filterUsers = []
          return
        }
        const ids = String(userIdParam).split(',').map(s => s.trim()).filter(Boolean)
        const list = this.userOptions || []
        this.filterUsers = ids.map(id => {
          const num = Number(id)
          const found = list.find(opt => opt.value && opt.value.id === num)
          return found ? found.value : { id: num }
        })
      },
      immediate: true,
    },
  },

  async mounted() {
    if (!this.$store.getters['generalData/assignList/assignListLoaded']) {
      await this.$store.dispatch('generalData/assignList/fetchAssignList')
    }
    if (!this.$store.getters['generalData/offices/officesLoaded']) {
      await this.$store.dispatch('generalData/offices/fetchOffices')
    }
  },

  methods: {
    // Same logic as FilterRequest: push to $route.query so buildParams sends params to API
    applyFilters() {
      const query = { ...this.$route.query }
      if (this.filterFrom) query.from = this.filterFrom
      else delete query.from
      if (this.filterTo) query.to = this.filterTo
      else delete query.to
      if (this.filterStatus) query.status = this.filterStatus
      else delete query.status
      if (this.filterOfficeId != null && this.filterOfficeId !== '') query.office = this.filterOfficeId
      else delete query.office
      const ids = this.filterUsers && this.filterUsers.length
        ? this.filterUsers.map(u => (u && u.id != null ? u.id : u)).filter(Boolean)
        : []
      if (ids.length) query.user_id = ids.join(',')
      else delete query.user_id

      this.$router.push({ query }).then(() => {
        if (this.$refs.generalTable) {
          this.$refs.generalTable.getAllData(1)
        }
      })
    },

    clearFilters() {
      this.filterFrom = ''
      this.filterTo = ''
      this.filterStatus = ''
      this.filterOfficeId = null
      this.filterUsers = []
      const query = { ...this.$route.query }
      delete query.from
      delete query.to
      delete query.status
      delete query.office
      delete query.user_id
      this.$router.push({ query }).then(() => {
        if (this.$refs.generalTable) {
          this.$refs.generalTable.getAllData(1)
        }
      })
    },

    computeStats(rows, apiData) {
      if (apiData && typeof apiData.count === 'number' && typeof apiData.approved_count === 'number') {
        this.totalRequests = apiData.count
        this.approvedCount = apiData.approved_count
        this.pendingCount = apiData.pending_count ?? 0
        this.rejectedCount = apiData.rejected_count ?? 0
        return
      }
      if (!rows || !rows.length) return
      this.totalRequests = rows.length
      this.approvedCount = rows.filter(r => r.status === 'approved').length
      this.pendingCount = rows.filter(r => r.status === 'pending').length
      this.rejectedCount = rows.filter(r => r.status === 'rejected').length
    },

    getAllDataPass(page) {
      this.$refs.generalTable.getAllData(page)
    },

    handleCurrentLeavesBalance(name) {
      currentLeavesBalance(name, this.$swal)
    },

    handleApproval(id) {
      openApproval(id, this.$swal, this.getAllDataPass, val => { this.loading = val })
    },

    handleFormData(date) {
      return formatDate(date)
    },

    getInitials(name) {
      if (!name) return '?'
      return name.split(' ').map(w => w[0]).join('')
        .substring(0, 2)
        .toUpperCase()
    },

    getTypeIcon(type) {
      const map = {
        annual: ['fas', 'umbrella-beach'],
        sick: ['fas', 'briefcase-medical'],
        emergency: ['fas', 'triangle-exclamation'],
        maternity: ['fas', 'baby'],
        unpaid: ['fas', 'money-bill-wave'],
      }
      return map[type] || ['fas', 'suitcase-rolling']
    },

    getStatusIcon(status) {
      const map = {
        approved: ['fas', 'circle-check'],
        pending: ['fas', 'clock'],
        rejected: ['fas', 'circle-xmark'],
        returned: ['fas', 'rotate-left'],
      }
      return map[status] || ['fas', 'circle-question']
    },
  },
}
</script>

<style scoped lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import './Schedule.scss';
</style>

<!-- Global: datepicker dropdown may be portaled to body -->
<style lang="scss">
.schedule-datepicker-dropdown {
  z-index: 9999 !important;
}
</style>
